import React, { useState, useEffect } from 'react'
import { Plus, Loader2, Check } from 'lucide-react'
import { Card, Button, Modal } from '../ui'
import {
  type Organization,
  organizationService,
  type OrganizationLimit,
  type Limit
} from '../../services/organizationService'
import { useToast } from '../../contexts/ToastContext'

interface OrganizationLimitsProps {
  organization: Organization
  onRefresh?: () => void
}

const OrganizationLimits: React.FC<OrganizationLimitsProps> = ({
  organization
}) => {
  const { showError, showSuccess } = useToast()
  const [limits, setLimits] = useState<OrganizationLimit[]>([])
  const [availableLimits, setAvailableLimits] = useState<Limit[]>([])
  const [loading, setLoading] = useState(true)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [selectedLimitId, setSelectedLimitId] = useState<string | null>(null)
  const [savingLimitId, setSavingLimitId] = useState<string | null>(null)
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [limitToUpdate, setLimitToUpdate] = useState<{
    limitId: string
    newValue: number
    limitName?: string
  } | null>(null)
  const [changeReason, setChangeReason] = useState('')
  const [tempValue, setTempValue] = useState<string>('')
  const [limitInputValues, setLimitInputValues] = useState<Map<string, string>>(
    new Map()
  )

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)

        const [orgLimitsResponse, availableLimitsResponse] = await Promise.all([
          organizationService.getOrganizationLimits(organization.id),
          organizationService.getAvailableLimits()
        ])

        if (orgLimitsResponse.success) {
          setLimits(orgLimitsResponse.data || [])
        }

        if (availableLimitsResponse.success) {
          setAvailableLimits(availableLimitsResponse.data || [])
        }
      } catch (error) {
        showError(
          'Failed to load limits',
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        )
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [organization.id, showError])

  const getAvailableLimitsToAdd = () => {
    const existingLimitIds = limits.map((l) => l.limitId)
    return availableLimits.filter((al) => !existingLimitIds.includes(al.id))
  }

  const handleAddLimitClick = () => {
    setIsAddingNew(true)
    setSelectedLimitId(null)
    setTempValue('')
  }

  const handleCancelAdd = () => {
    setIsAddingNew(false)
    setSelectedLimitId(null)
    setTempValue('')
  }

  const handleAddLimit = async () => {
    if (!selectedLimitId) {
      showError(
        'Please select a limit',
        'You must select a limit from the dropdown'
      )
      return
    }

    if (!tempValue || tempValue.trim() === '') {
      showError('Value required', 'Please enter a limit value')
      return
    }

    const numValue = parseInt(tempValue)
    if (isNaN(numValue) || numValue < -1) {
      showError(
        'Invalid value',
        'Limit value must be -1 (unlimited) or a positive number'
      )
      return
    }

    try {
      setSavingLimitId(selectedLimitId)

      // Close the add form immediately and show loading state
      const limitIdToAdd = selectedLimitId
      const valueToAdd = numValue
      setIsAddingNew(false)
      setSelectedLimitId(null)
      setTempValue('')

      // Add limit using POST endpoint
      const response = await organizationService.addOrganizationLimit(
        organization.id,
        limitIdToAdd,
        valueToAdd,
        'Added by admin'
      )

      if (response.success) {
        showSuccess('Limit added successfully')
        // Refresh limits list to show updated data
        const orgLimitsResponse =
          await organizationService.getOrganizationLimits(organization.id)
        if (orgLimitsResponse.success) {
          setLimits(orgLimitsResponse.data)
        }
      } else {
        throw new Error('Failed to add limit')
      }
    } catch (error) {
      console.error('Error adding limit:', error)
      showError(
        'Failed to add limit',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      )
      // Refresh limits list even on error to ensure UI is in sync
      try {
        const orgLimitsResponse =
          await organizationService.getOrganizationLimits(organization.id)
        if (orgLimitsResponse.success) {
          setLimits(orgLimitsResponse.data)
        }
      } catch (refreshError) {
        console.error('Error refreshing limits:', refreshError)
      }
    } finally {
      setSavingLimitId(null)
    }
  }

  const handleUpdateLimitClick = (
    limitId: string,
    newValue: string,
    limitName?: string
  ) => {
    if (!newValue || newValue.trim() === '') {
      showError('Value required', 'Please enter a limit value')
      return
    }

    const numValue = parseInt(newValue)
    if (isNaN(numValue) || numValue < -1) {
      showError(
        'Invalid value',
        'Limit value must be -1 (unlimited) or a positive number'
      )
      return
    }

    setLimitToUpdate({ limitId, newValue: numValue, limitName })
    setShowReasonModal(true)
  }

  const handleConfirmUpdate = async () => {
    if (!limitToUpdate) return

    if (!changeReason.trim()) {
      showError('Reason required', 'Please provide a reason for this change')
      return
    }

    try {
      setSavingLimitId(limitToUpdate.limitId)

      // Close modal immediately and show loader on the limit
      setShowReasonModal(false)
      const updateInfo = limitToUpdate
      const reason = changeReason.trim()
      setLimitToUpdate(null)
      setChangeReason('')

      const response = await organizationService.updateOrganizationLimit(
        organization.id,
        updateInfo.limitId,
        updateInfo.newValue,
        reason,
        undefined,
        updateInfo.limitName
      )

      if (response.success) {
        showSuccess('Limit updated successfully')
        // Refresh limits list to show updated data
        const orgLimitsResponse =
          await organizationService.getOrganizationLimits(organization.id)
        if (orgLimitsResponse.success) {
          setLimits(orgLimitsResponse.data)
        }
      } else {
        throw new Error('Failed to update limit')
      }
    } catch (error) {
      console.error('Error updating limit:', error)
      showError(
        'Failed to update limit',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      )
      // Refresh limits list even on error to ensure UI is in sync
      try {
        const orgLimitsResponse =
          await organizationService.getOrganizationLimits(organization.id)
        if (orgLimitsResponse.success) {
          setLimits(orgLimitsResponse.data)
        }
      } catch (refreshError) {
        console.error('Error refreshing limits:', refreshError)
      }
    } finally {
      setSavingLimitId(null)
    }
  }

  const formatNumber = (num: number) => {
    if (num === -1) return 'Unlimited'
    return new Intl.NumberFormat().format(num)
  }

  const availableToAdd = getAvailableLimitsToAdd()

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='flex items-center justify-between pb-3 border-b border-gray-200'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>Limits</h3>
          <p className='text-xs text-gray-500 mt-0.5'>
            {limits.length} limit{limits.length !== 1 ? 's' : ''} configured
          </p>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary'></div>
        </div>
      ) : (
        <div className='space-y-3'>
          {/* Limits List */}
          {limits.length > 0 && (
            <div className='space-y-2'>
              {limits.map((limit) => {
                const isUnlimited = limit.limitValue === -1
                const isOverBaseline =
                  !isUnlimited && limit.limitValue > limit.tierBaselineValue
                const isSaving = savingLimitId === limit.limitId
                const limitName = limit.limitDisplayName || 'Unknown Limit'

                return (
                  <Card
                    key={limit.id}
                    variant='outlined'
                    className={`!p-4 transition-all ${
                      isSaving ? 'opacity-60' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className='flex items-center gap-3'>
                      {/* Limit Info */}
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 mb-2'>
                          <p className='text-sm font-medium text-gray-900 truncate'>
                            {limitName}
                          </p>
                          {isOverBaseline && (
                            <span className='text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded'>
                              {`+$${limit.additionalMonthlyCost}/mo`}
                            </span>
                          )}
                        </div>
                        <div className='space-y-1.5'>
                          <div className='flex items-center gap-2'>
                            <span className='text-xs text-gray-500 w-24'>
                              Tier baseline:
                            </span>
                            <span className='text-xs font-medium text-gray-700'>
                              {formatNumber(limit.tierBaselineValue)}
                            </span>
                          </div>
                          <div className='flex items-center gap-2'>
                            <span className='text-xs text-gray-500 w-24'>
                              Current limit:
                            </span>
                            <span className='text-xs font-medium text-gray-700'>
                              {formatNumber(limit.limitValue)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Update input */}
                      <div className='flex items-center gap-1'>
                        <input
                          type='number'
                          value={
                            limitInputValues.get(limit.limitId!) ??
                            limit.limitValue.toString()
                          }
                          onChange={(e) => {
                            const newValues = new Map(limitInputValues)
                            newValues.set(limit.limitId!, e.target.value)
                            setLimitInputValues(newValues)
                          }}
                          placeholder='New value'
                          className='w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-avi-primary focus:border-transparent'
                          disabled={isSaving}
                        />
                        <button
                          onClick={() => {
                            const value = limitInputValues.get(limit.limitId!)
                            if (value && value.trim() !== '') {
                              handleUpdateLimitClick(
                                limit.limitId!,
                                value,
                                limit.limitName
                              )
                              const newValues = new Map(limitInputValues)
                              newValues.delete(limit.limitId!)
                              setLimitInputValues(newValues)
                            }
                          }}
                          disabled={
                            isSaving ||
                            !limitInputValues.get(limit.limitId!) ||
                            limitInputValues.get(limit.limitId!)?.trim() ===
                              '' ||
                            limitInputValues.get(limit.limitId!) ===
                              limit.limitValue.toString()
                          }
                          className='px-2 py-1 text-xs font-medium text-white bg-avi-primary hover:bg-avi-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors flex items-center gap-1'
                        >
                          {isSaving ? (
                            <Loader2 className='h-3 w-3 animate-spin' />
                          ) : (
                            <Check className='h-3 w-3' />
                          )}
                        </button>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>
          )}

          {/* Add Limit Section */}
          {availableToAdd.length > 0 && !isAddingNew && (
            <button
              onClick={handleAddLimitClick}
              className='w-full py-2.5 px-3 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-dashed border-gray-300 hover:border-gray-400 rounded-md transition-colors flex items-center justify-center gap-1.5'
            >
              <Plus className='h-3.5 w-3.5' />
              Add limit
            </button>
          )}

          {isAddingNew && (
            <Card variant='outlined' className='p-3 bg-gray-50 border-gray-300'>
              <div className='space-y-2'>
                <label className='block text-xs font-medium text-gray-700'>
                  Limit Type
                </label>
                <select
                  value={selectedLimitId || ''}
                  onChange={(e) => setSelectedLimitId(e.target.value)}
                  className='w-full px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-avi-primary focus:border-transparent'
                  disabled={availableToAdd.length === 0}
                  autoFocus
                >
                  <option value=''>Select a limit to add...</option>
                  {availableToAdd.map((limit) => (
                    <option key={limit.id} value={limit.id}>
                      {limit.displayName}
                    </option>
                  ))}
                </select>
                <label className='block text-xs font-medium text-gray-700'>
                  Value (-1 for unlimited)
                </label>
                <input
                  type='number'
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder='Enter value'
                  className='w-full px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-avi-primary focus:border-transparent'
                />
                <div className='flex gap-2'>
                  <button
                    onClick={handleAddLimit}
                    disabled={!selectedLimitId || !tempValue}
                    className='flex-1 px-3 py-1.5 text-xs font-medium text-white bg-avi-primary hover:bg-avi-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors'
                  >
                    Add
                  </button>
                  <button
                    onClick={handleCancelAdd}
                    className='px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 rounded transition-colors'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Card>
          )}

          {/* Info message */}
          {availableToAdd.length === 0 && !isAddingNew && limits.length > 0 && (
            <p className='text-xs text-gray-500 text-center py-2'>
              All available limits have been added
            </p>
          )}

          {limits.length > 0 && (
            <div className='px-3 py-2 bg-blue-50 border border-blue-200 rounded-md'>
              <p className='text-xs text-blue-800'>
                Enter a new value and click the checkmark button to update. Use
                -1 for unlimited values.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Reason Modal */}
      <Modal
        isOpen={showReasonModal}
        onClose={() => {
          setShowReasonModal(false)
          setLimitToUpdate(null)
          setChangeReason('')
        }}
        title='Update Limit'
      >
        <div className='space-y-3'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1.5'>
              Reason for change
            </label>
            <textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder='Explain why you are changing this limit...'
              rows={3}
              className='w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
              autoFocus
            />
          </div>
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setShowReasonModal(false)
                setLimitToUpdate(null)
                setChangeReason('')
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUpdate}
              disabled={!changeReason.trim()}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default OrganizationLimits
